import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import UserCard from "../../components/SuperDashboard/Users/UserCard";
import DeleteModal from "../../components/SuperDashboard/Users/DeleteModal";
import CreateUserModal from "../../components/SuperDashboard/Users/CreateUserModal";
import EditUserModal from "../../components/SuperDashboard/Users/UpdateUserModal";
import MigrateAllOffersModal from "../../components/SuperDashboard/Users/MigrateAllOffersModal";
import UpdateFiltersModal from "../../components/SuperDashboard/Users/UpdateFiltersModal";

import { User } from "../../models/users";
import { UserFilterDefaults, UserFilterDefaultsUpdate } from "../../models/usersFilters";
import { getAllUsers, deleteUser, register } from "../../api/users";
import { getUserOffers, migrateAllOffers } from "../../api/offers";
import { getUserFilterByEmail, updateUserFilterByEmail } from "../../api/userFilters";

export default function SuperManageUsersPage() {
    const [users, setUsers] = useState<User[]>([]);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [editingUser, setEditingUser] = useState<User | null>(null);
    const [initialFilters, setInitialFilters] = useState<UserFilterDefaults | undefined>();

    const [isCreateOpen, setCreateOpen] = useState(false);
    const [isEditOpen, setEditOpen] = useState(false);
    const [isDeleteOpen, setDeleteOpen] = useState(false);
    const [isMigrateAllOpen, setMigrateAllOpen] = useState(false);
    const [isUpdateFiltersOpen, setUpdateFiltersOpen] = useState(false);

    const fetchUsers = async () => {
        try {
            const data = await getAllUsers();
            setUsers(data);
        } catch (error: any) {
            toast.error(error?.message || "Failed to fetch users");
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    const handleEdit = (user: User) => {
        setEditingUser(user);
        setEditOpen(true);
    };

    const handleDelete = async (user: User) => {
        try {
            const offers = await getUserOffers(user.email);
            if (offers.length > 0) {
                toast.error("Cannot delete user: they have associated offers.");
                return;
            }
            setSelectedUser(user);
            setDeleteOpen(true);
        } catch {
            toast.error("Failed to check user's offers.");
        }
    };

    const handleMigrateAll = (user: User) => {
        setSelectedUser(user);
        setMigrateAllOpen(true);
    };

    const handleUpdateFilters = async (user: User) => {
        setSelectedUser(user);
        try {
            const filters = await getUserFilterByEmail(user.email);
            setInitialFilters(filters);
        } catch {
            setInitialFilters({
                email: user.email,
                verticals: [],
                state_list: [],
                min_duration: undefined,
                max_duration: undefined,
                min_payout: undefined,
                max_payout: undefined,
            }); // user has no filters yet, use defaults
        } finally {
            setUpdateFiltersOpen(true);
        }
    };

    const handleDeleteConfirm = async () => {
        if (!selectedUser) return;
        try {
            await deleteUser(selectedUser.email);
            setUsers((prev) => prev.filter((u) => u.email !== selectedUser.email));
        } catch (error: any) {
            toast.error(error?.message || "Failed to delete user");
        } finally {
            setSelectedUser(null);
            setDeleteOpen(false);
        }
    };

    const handleMigrateAllConfirm = async (receiverEmail: string) => {
        if (!selectedUser) return;
        try {
            await migrateAllOffers({ old_email: selectedUser.email, new_email: receiverEmail });
            toast.success("Migrated all offers successfully");
        } catch (error: any) {
            toast.error(error?.message || "Failed to migrate offers");
        } finally {
            setSelectedUser(null);
            setMigrateAllOpen(false);
        }
    };

    const handleUpdateFiltersConfirm = async (updates: UserFilterDefaultsUpdate) => {
        if (!selectedUser) return;
        try {
            await updateUserFilterByEmail(updates);
            toast.success("Filters updated successfully");
        } catch (error: any) {
            toast.error(error?.message || "Failed to update filters");
        } finally {
            setSelectedUser(null);
            setUpdateFiltersOpen(false);
        }
    };

    const handleUserUpdated = (updatedUser: User) => {
        setUsers((prev) =>
            prev.map((u) => (u.email === updatedUser.email ? updatedUser : u))
        );
    };

    return (
        <>
            <div className="p-6 min-h-screen bg-gray-100 dark:bg-gray-900">
                <div className="flex justify-between items-center mb-6">
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                        Manage Users
                    </h1>
                    <button
                        onClick={() => setCreateOpen(true)}
                        className="px-4 py-2 bg-green-800 text-white rounded-lg hover:bg-green-900"
                    >
                        + Create User
                    </button>
                </div>

                {users.map((user) => (
                    <UserCard
                        key={user.email}
                        email={user.email}
                        role={user.role}
                        onEdit={() => handleEdit(user)}
                        onDelete={() => handleDelete(user)}
                        onMigrateAll={() => handleMigrateAll(user)}
                        onUpdateFilters={() => handleUpdateFilters(user)}
                    />
                ))}
            </div>

            <CreateUserModal
                isOpen={isCreateOpen}
                onClose={() => setCreateOpen(false)}
                onConfirm={async (newUser) => {
                    try {
                        const created = await register(newUser);
                        setUsers((prev) => [...prev, created]);
                        setCreateOpen(false);
                    } catch (error: any) {
                        toast.error(error?.response?.data?.detail || "Failed to create user");
                    }
                }}
            />

            {editingUser && (
                <EditUserModal
                    isOpen={isEditOpen}
                    onClose={() => { setEditOpen(false); setSelectedUser(null); }}
                    user={editingUser}
                    onUserUpdated={handleUserUpdated}
                />
            )}

            <DeleteModal
                isOpen={isDeleteOpen}
                email={selectedUser?.email}
                onClose={() => { setDeleteOpen(false); setSelectedUser(null); }}
                onConfirm={handleDeleteConfirm}
            />

            <MigrateAllOffersModal
                isOpen={isMigrateAllOpen}
                email={selectedUser?.email}
                onClose={() => { setMigrateAllOpen(false); setSelectedUser(null); }}
                onConfirm={handleMigrateAllConfirm}
            />

            <UpdateFiltersModal
                isOpen={isUpdateFiltersOpen}
                email={selectedUser?.email}
                initialFilters={initialFilters}
                onClose={() => { setUpdateFiltersOpen(false); setSelectedUser(null); }}
                onConfirm={handleUpdateFiltersConfirm}
            />
        </>
    );
}
